import { NotificationTypes } from '../../../primary-record-types';
import React, { useEffect } from 'react';
import getDateString from '../../../utils/getDateString';

const getKeyFromNotification = (notification: NotificationTypes.ReadOrUnreadInAppNotification) => {
    return `${notification.notificationType}-${notification.dateTime}-${notification.read}`;
};

export type NotificationReadStatusFilterValue = 'read' | 'unread' | 'all';
export type NotificationSortOrder = 'newest' | 'oldest';

export type UseNotificationListStateArgs = {
    notifications: NotificationTypes.ReadOrUnreadInAppNotification[];
    onPersistReadUnread: (notifications: NotificationTypes.ReadOrUnreadInAppNotification[]) => void;
};

export type UseNotificationListStateReturnValue = {
    getIsSelected: (notification: NotificationTypes.ReadOrUnreadInAppNotification) => boolean;
    isSelecting: boolean;
    toggleIsSelecting: () => void;
    canMarkRead: boolean;
    canMarkUnread: boolean;
    markRead: () => void;
    markUnread: () => void;
    onToggleSelect: (notification: NotificationTypes.ReadOrUnreadInAppNotification) => void;
    canMarkAllRead: boolean;
    onMarkAllRead: () => void;
    canDeselectAll: boolean;
    onDeselectAll: () => void;
    searchText: string;
    onSearchTextChange: (value: string) => void;
    displayNotifications: NotificationTypes.ReadOrUnreadInAppNotification[];
    hasAnyProfileNotifications: boolean;
    readStatusFilterValue: NotificationReadStatusFilterValue;
    onReadStatusFilterValueChange: (value: NotificationReadStatusFilterValue) => void;
    sortOrder: NotificationSortOrder;
    onSortOrderChange: (value: NotificationSortOrder) => void;
};

const filterBySearch = (
    notifications: NotificationTypes.ReadOrUnreadInAppNotification[],
    searchText: string,
    readStatusFilter: NotificationReadStatusFilterValue,
) => {
    if (!searchText && readStatusFilter === 'all') {
        return notifications;
    }

    const lowerCaseSearchText = searchText.toLowerCase();

    return notifications
        .map((notification) => {
            const searchTexts = [
                notification.firstName,
                notification.lastName,
                getDateString(notification.dateTime, 'date-time'),
            ];

            if (notification.notificationType === 'medical-profile-message') {
                searchTexts.push(notification.auditLogTitle);
            } else if (notification.notificationType === 'medical-profile-activity-comment') {
                searchTexts.push(notification.commentContent, notification.messageContent);
            } else if (notification.notificationType === 'medical-profile-note-comment') {
                searchTexts.push(notification.noteTitle, notification.commentContent);
            }

            return {
                searchTexts: searchTexts.filter((text) => !!text).map((text) => text.toLowerCase()),
                notification,
            };
        })
        .filter(({ searchTexts }) => {
            return searchTexts.some((text) => text.includes(lowerCaseSearchText));
        })
        .filter(({ notification }) => {
            if (readStatusFilter === 'all') {
                return true;
            }

            return readStatusFilter === 'read' ? notification.read : !notification.read;
        })
        .map(({ notification }) => notification);
};

export const useNotificationListState = ({
    notifications,
    onPersistReadUnread,
}: UseNotificationListStateArgs): UseNotificationListStateReturnValue => {
    const [isSelecting, setIsSelecting] = React.useState(false);
    const [searchText, setSearchText] = React.useState('');
    const [selectedNotificationIds, setSelectedNotificationIds] = React.useState<string[]>([]);
    const [readStatusFilter, setReadStatusFilter] = React.useState<NotificationReadStatusFilterValue>('all');
    const [sortOrder, setSortOrder] = React.useState<NotificationSortOrder>('newest');
    useEffect(() => {
        setSelectedNotificationIds([]);
        setIsSelecting(false);
        setSearchText('');
        const hasUnread = notifications.some((notification) => !notification.read);
        setReadStatusFilter(hasUnread ? 'unread' : 'all');
        setSortOrder('newest');
    }, [notifications]);

    const selectedNotifications = React.useMemo(() => {
        return notifications.filter((notification) =>
            selectedNotificationIds.includes(getKeyFromNotification(notification)),
        );
    }, [notifications, selectedNotificationIds]);

    const anySelected = React.useMemo(() => {
        return selectedNotificationIds.length > 0;
    }, [selectedNotificationIds]);

    const areSelectedAllRead = React.useMemo(() => {
        return anySelected && selectedNotifications.every((notification) => notification.read);
    }, [selectedNotifications, anySelected]);

    const areSelectedAllUnread = React.useMemo(() => {
        return anySelected && selectedNotifications.every((notification) => !notification.read);
    }, [selectedNotifications, anySelected]);

    const onToggleSelect = React.useCallback(
        (notification: NotificationTypes.ReadOrUnreadInAppNotification) => {
            const key = getKeyFromNotification(notification);
            setSelectedNotificationIds((prev) => {
                if (prev.includes(key)) {
                    return prev.filter((id) => id !== key);
                } else {
                    return [...prev, key];
                }
            });
        },
        [setSelectedNotificationIds],
    );

    const canMarkRead = React.useMemo(() => {
        return anySelected && areSelectedAllUnread && isSelecting;
    }, [anySelected, areSelectedAllUnread, isSelecting]);

    const canMarkUnread = React.useMemo(() => {
        return anySelected && areSelectedAllRead && isSelecting;
    }, [anySelected, areSelectedAllRead, isSelecting]);

    const markRead = React.useCallback(() => {
        if (canMarkRead) {
            onPersistReadUnread(selectedNotifications);
        }
    }, [canMarkRead, onPersistReadUnread, selectedNotifications]);

    const markUnread = React.useCallback(() => {
        if (canMarkUnread) {
            onPersistReadUnread(selectedNotifications);
        }
    }, [canMarkUnread, onPersistReadUnread, selectedNotifications]);

    const getIsSelected = React.useCallback(
        (notification: NotificationTypes.ReadOrUnreadInAppNotification) => {
            return selectedNotificationIds.includes(getKeyFromNotification(notification));
        },
        [selectedNotificationIds],
    );

    const toggleIsSelecting = React.useCallback(() => {
        setSelectedNotificationIds([]);
        setIsSelecting((prev) => !prev);
    }, []);

    const canMarkAllRead = React.useMemo(() => {
        return isSelecting && !anySelected && notifications.some((notification) => !notification.read);
    }, [anySelected, isSelecting, notifications]);

    const onMarkAllRead = React.useCallback(() => {
        if (canMarkAllRead) {
            const allUnread = notifications.filter((notification) => !notification.read);

            onPersistReadUnread(allUnread);
        }
    }, [canMarkAllRead, notifications, onPersistReadUnread]);

    const canDeselectAll = React.useMemo(() => {
        return isSelecting && anySelected;
    }, [isSelecting, anySelected]);

    const onDeselectAll = React.useCallback(() => {
        setSelectedNotificationIds([]);
    }, [setSelectedNotificationIds]);

    const displayNotifications = React.useMemo(() => {
        const filtered = filterBySearch(notifications, searchText, readStatusFilter);
        return [...filtered].sort((a, b) => {
            const aTime = new Date(a.dateTime).getTime();
            const bTime = new Date(b.dateTime).getTime();
            return sortOrder === 'newest' ? bTime - aTime : aTime - bTime;
        });
    }, [notifications, searchText, readStatusFilter, sortOrder]);

    return {
        getIsSelected,
        isSelecting,
        toggleIsSelecting,
        canMarkRead,
        canMarkUnread,
        markRead,
        markUnread,
        onToggleSelect,
        canMarkAllRead,
        onMarkAllRead,
        canDeselectAll,
        onDeselectAll,
        displayNotifications,
        searchText,
        onSearchTextChange: setSearchText,
        hasAnyProfileNotifications: notifications.length > 0,
        readStatusFilterValue: readStatusFilter,
        onReadStatusFilterValueChange: setReadStatusFilter,
        sortOrder,
        onSortOrderChange: setSortOrder,
    };
};
